import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Button, Loader } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
// utils
import media from 'utils/mediaQueries';
// actions
import { fetchEventbriteOrganizations, fetchEventbriteVideos } from 'ducks/merchantDucks/externalAccount/actions';
import { deleteItemMetadata, setShouldCloseSection, updateItemMetadata } from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';
// selectors
import {
  getEventbriteOrganizations,
  getEventbriteVideos,
  getIsFetching,
} from 'ducks/merchantDucks/externalAccount/selectors';
// types
import { Optional } from 'utils/types';
import { Event } from 'ducks/merchantDucks/externalAccount/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
// components
import BoxItemDefaultGrid from 'components/BoxItemDefaultGrid';
import AccordionFooter from 'components/AccordionFooter';
import ConfirmModal from 'components/Modals/ConfirmModal';
import EventbriteEvent from 'pages/MerchantPages/Assets/components/Eventbrite/EventbriteEvent';
import EventbriteOrganizationsTable from 'pages/MerchantPages/Assets/components/Eventbrite/EventbriteOrganizationsTable';
import { CustomButton } from 'pages/MerchantPages/Assets/Price/containers/PriceOptions';
import { BlueTypography } from 'components/SharedStyledElements';
import { SectionType, AccordionHelper } from 'pages/MerchantPages/Assets/components/AccordionHelper';

interface Props {
  closePanel: () => void;
  itemType: string;
  assetMetadata: Record<string, string>;
  assetId: number;
  accordionLabel: string;
}

export interface EventbriteMetadata {
  eventbrite_relation?: boolean;
  eventbrite_organization_id?: number | null;
  eventbrite_event_id?: number | null;
  eventbrite_organization_name?: string | null;
  eventbrite_event_name?: string | null;
  eventbrite_webhook_id?: number;
}

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  margin: 0 auto;
  padding-top: 2rem;
  position: relative;
`;

const EventsGrid = styled(BoxItemDefaultGrid)`
  grid-template-columns: repeat(3, minmax(31%, 1fr));

  ${media.customMaxWidth(1430)} {
    grid-template-columns: repeat(2, minmax(48%, 1fr));
  }
`;

const Wrapper = styled.div`
  margin: 2rem;
`;
const OrganizationContainer = styled.div`
  margin: 0.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Title = styled(BlueTypography)`
  margin-left: 0.5rem;
`;

const EventbriteContainer: React.FC<Props> = ({ closePanel, itemType, assetMetadata, assetId, accordionLabel }) => {
  const dispatch = useDispatch();
  const eventbriteOrganizations = useSelector(getEventbriteOrganizations);
  const eventbriteVideos = useSelector(getEventbriteVideos);
  const isFetching = useSelector(getIsFetching);

  const [tab, changeTab] = useState(0);
  const [currentEventId, changeCurrentEventId] = useState<Optional<number>>(null);
  const [currentOrganizationId, changeCurrentOrganizationId] = useState<Optional<number>>(null);
  const [currentEventName, changeCurrentEventName] = useState<string>('');
  const [currentOrganizationName, changeCurrentOrganizationName] = useState<string>('');

  const { organizations, pagination: organizationsPagination } = eventbriteOrganizations;
  const { events, pagination: eventsPagination } = eventbriteVideos;

  const eventbriteMetadata: EventbriteMetadata = {
    eventbrite_relation: true,
    eventbrite_organization_id: currentOrganizationId,
    eventbrite_event_id: currentEventId,
    eventbrite_organization_name: currentOrganizationName,
    eventbrite_event_name: currentEventName,
  };

  const metadataForDeletion: Array<keyof EventbriteMetadata> = [
    ...(Object.keys(eventbriteMetadata) as Array<keyof EventbriteMetadata>),
    'eventbrite_webhook_id',
  ];

  useEffect(() => {
    dispatch(fetchEventbriteOrganizations());
  }, [fetchEventbriteOrganizations]);

  const fetchEvents = (organizationId: number) => {
    dispatch(fetchEventbriteVideos(organizationId));
  };

  const handleTabChange = (index: number) => {
    changeTab(index);
    if (index === 0) {
      changeCurrentOrganizationId(null);
    }
  };

  const handleSave = async () => {
    await dispatch(updateItemMetadata({ metadata: eventbriteMetadata, itemType, isExternalAccountMetadata: true }));
    handleTabChange(0);
  };

  const openDeleteModal = () => {
    dispatch(openModal(ModalNames.DeleteMetadata));
  };

  const handleCurrentEventId = (id: number, name: string) => {
    if (currentEventId === id) {
      changeCurrentEventId(null);
      changeCurrentEventName('');
      dispatch(setShouldCloseSection(true));
    } else {
      changeCurrentEventId(id);
      changeCurrentEventName(name);
      dispatch(setShouldCloseSection(false));
    }
  };

  const loadMoreOrganizations = () => {
    dispatch(fetchEventbriteOrganizations(organizationsPagination.continuation));
  };

  const loadMoreEvents = () => {
    if (currentOrganizationId) {
      dispatch(fetchEventbriteVideos(currentOrganizationId, eventsPagination.continuation));
    }
  };

  const handleDelete = async () => {
    await dispatch(
      deleteItemMetadata({
        metadataKeys: metadataForDeletion,
        itemType,
        assetId,
      })
    );
    closePanel();
  };

  const organizationsPreview = (
    <>
      {isFetching ? (
        <Loader />
      ) : (
        <EventbriteOrganizationsTable
          handleCurrentOrganizationId={changeCurrentOrganizationId}
          changeCurrentOrganizationName={changeCurrentOrganizationName}
          organizationsData={organizations}
          fetchEvents={fetchEvents}
          handleTabChange={handleTabChange}
        />
      )}

      {organizationsPagination.has_more_items && organizationsPagination.continuation && (
        <ButtonContainer>
          <Button onClick={loadMoreOrganizations}>Load more</Button>
        </ButtonContainer>
      )}
    </>
  );

  const loadMoreButton = eventsPagination.has_more_items && currentOrganizationId && eventsPagination.continuation && (
    <ButtonContainer>
      <Button onClick={loadMoreEvents}>Load more</Button>
    </ButtonContainer>
  );

  const eventsContent =
    events && events.length ? (
      <>
        <EventsGrid>
          {events.map((event: Event) => (
            <EventbriteEvent
              key={event.id}
              event={event}
              handleCurrentEvent={handleCurrentEventId}
              currentEvent={currentEventId}
            />
          ))}
        </EventsGrid>
        {loadMoreButton}
      </>
    ) : (
      <Typography variant="p">There are currently no events for this organization</Typography>
    );

  const eventsPreview = (
    <>
      <CustomButton buttonModifiers={['buttonLink']} type="button" onClick={() => handleTabChange(0)}>
        &larr; back
      </CustomButton>
      <ItemsContainer>{isFetching ? <Loader /> : eventsContent}</ItemsContainer>
    </>
  );
  const {
    eventbrite_event_id: eventId,
    eventbrite_event_name: eventName,
    eventbrite_organization_id: id,
    eventbrite_organization_name: name,
  } = assetMetadata;

  const previewRelatedEvent = (
    <EventbriteOrganizationsTable
      organizationsData={[
        {
          id,
          name,
          eventId,
          eventName,
        },
      ]}
      isEventRelated
      openDeleteModal={openDeleteModal}
      handleDelete={handleDelete}
    />
  );
  const isEventRelated = id && name && eventId && eventName;
  const displayTitle = isEventRelated ? 'Related Eventbrite event' : 'Eventbrite organizations';
  const displayPreview = isEventRelated ? previewRelatedEvent : organizationsPreview;

  return (
    <>
      <AccordionHelper closePanel={closePanel} index={SectionType.EventBriteEvent} submitForm={handleSave} />
      <ContentContainer>
        <Wrapper>
          <Title variant="h6">{tab === 0 ? displayTitle : `${currentOrganizationName}'s Eventbrite events`}</Title>
          <OrganizationContainer>{tab === 0 ? displayPreview : eventsPreview}</OrganizationContainer>
        </Wrapper>
        {tab === 1 && (
          <>
            <AccordionFooter
              accordionLabel={accordionLabel}
              handleCancel={closePanel}
              onFormSubmit={handleSave}
              buttonType="button"
              disabled={!currentEventId}
            />
          </>
        )}
      </ContentContainer>
      <ConfirmModal
        modalName={ModalNames.DeleteMetadata}
        message="Are you sure you want to delete this relation?"
        handleConfirm={handleDelete}
      />
    </>
  );
};

export default EventbriteContainer;
